// src/views/ComparisonPage.js
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, CheckCircle2, XCircle, AlertCircle, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';

const APP_SIGNUP_URL = "https://app.spatchy.co/pricing";

// ─── DATA ────────────────────────────────────────────────────────────────────
// All Jobber data sourced from getjobber.com/pricing, March 2026.
// Prices in USD as published. Spatchy prices in CAD.

const COMPARISON_ROWS = [
  {
    category: "Pricing",
    features: [
      {
        label: "Currency",
        spatchy: "CAD — no exchange rate surprises",
        jobber: "USD only",
        spatchy_yes: true,
        jobber_yes: false,
        jobber_note: "Canadian businesses pay in USD every month",
      },
      {
        label: "Solo plan / 1 user",
        spatchy: "$59 CAD/mo",
        jobber: "$29 USD/mo (~$41 CAD)",
        spatchy_yes: true,
        jobber_yes: true,
        jobber_note: "Annual billing required for lowest price",
      },
      {
        label: "5-user team plan",
        spatchy: "$149 CAD/mo",
        jobber: "$65 USD/mo (~$91 CAD) — Connect plan",
        spatchy_yes: true,
        jobber_yes: true,
        jobber_note: "Several key features locked out at this tier",
      },
      {
        label: "10-user team plan",
        spatchy: "$299 CAD/mo",
        jobber: "$97 USD/mo (~$136 CAD) — Grow plan",
        spatchy_yes: true,
        jobber_yes: true,
        jobber_note: "Grow plan required to access most advanced features",
      },
      {
        label: "No feature gating by tier",
        spatchy: "Every feature on every plan",
        jobber: "Features split across tiers",
        spatchy_yes: true,
        jobber_yes: false,
        jobber_note: "Must upgrade to unlock core tools",
      },
    ],
  },
  {
    category: "Core Operations",
    features: [
      {
        label: "Scheduling & dispatch",
        spatchy: "Included on all plans",
        jobber: "Included on all plans",
        spatchy_yes: true,
        jobber_yes: true,
      },
      {
        label: "Native mobile app (iOS & Android)",
        spatchy: "Included on all plans",
        jobber: "Included on all plans",
        spatchy_yes: true,
        jobber_yes: true,
      },
      {
        label: "Route optimization",
        spatchy: "Included on all plans — daily & weekly, Google Maps navigation",
        jobber: "Grow plan and above only",
        spatchy_yes: true,
        jobber_yes: false,
        jobber_note: "Requires $97 USD/mo Grow plan minimum",
      },
      {
        label: "Quotes & estimates",
        spatchy: "Included on all plans",
        jobber: "Included on all plans",
        spatchy_yes: true,
        jobber_yes: true,
      },
      {
        label: "Invoicing",
        spatchy: "Included on all plans",
        jobber: "Included on all plans",
        spatchy_yes: true,
        jobber_yes: true,
      },
      {
        label: "CRM & client management",
        spatchy: "Included on all plans",
        jobber: "Included on all plans",
        spatchy_yes: true,
        jobber_yes: true,
      },
      {
        label: "Customer portal",
        spatchy: "Included on all plans",
        jobber: "Included on all plans",
        spatchy_yes: true,
        jobber_yes: true,
      },
      {
        label: "Time tracking",
        spatchy: "Included on all plans",
        jobber: "Connect plan and above only",
        spatchy_yes: true,
        jobber_yes: false,
        jobber_note: "Requires $65 USD/mo Connect plan minimum",
      },
      {
        label: "Expense tracking",
        spatchy: "Included on all plans",
        jobber: "Connect plan and above only",
        spatchy_yes: true,
        jobber_yes: false,
        jobber_note: "Requires $65 USD/mo Connect plan minimum",
      },
      {
        label: "Job costing",
        spatchy: "Included on all plans",
        jobber: "Grow plan only",
        spatchy_yes: true,
        jobber_yes: false,
        jobber_note: "Requires $97 USD/mo Grow plan minimum",
      },
      {
        label: "QuickBooks sync",
        spatchy: "Included on all plans",
        jobber: "Connect plan and above only",
        spatchy_yes: true,
        jobber_yes: false,
        jobber_note: "Requires $65 USD/mo Connect plan minimum",
      },
    ],
  },
  {
    category: "Communication & Automation",
    features: [
      {
        label: "Automated invoice reminders",
        spatchy: "Included on all plans",
        jobber: "Connect plan and above only",
        spatchy_yes: true,
        jobber_yes: false,
        jobber_note: "Requires $65 USD/mo Connect plan minimum",
      },
      {
        label: "Automated quote follow-ups",
        spatchy: "Included on all plans",
        jobber: "Connect plan and above only",
        spatchy_yes: true,
        jobber_yes: false,
        jobber_note: "Requires $65 USD/mo Connect plan minimum",
      },
      {
        // wave1-fix e2b6f184: no custom rule/workflow builder exists — only a fixed invoice-automation settings form; stop claiming parity
        label: "Custom automation builder",
        spatchy: "Configurable invoice automation (overdue reminders, late fees, follow-ups)",
        jobber: "Grow plan only",
        spatchy_yes: false,
        jobber_yes: false,
        jobber_note: "Requires $97 USD/mo Grow plan minimum",
      },
      {
        label: "Email marketing campaigns",
        spatchy: "Included on all plans",
        jobber: "Add-on: +$79 USD/mo",
        spatchy_yes: true,
        jobber_yes: false,
        jobber_note: "Marketing Suite sold separately on all plans",
      },
    ],
  },
  {
    category: "Team & HR",
    features: [
      {
        label: "Employee profiles",
        spatchy: "Full digital employee files included",
        jobber: "Basic team management included",
        spatchy_yes: true,
        jobber_yes: true,
      },
      {
        label: "Hiring pipeline / ATS",
        spatchy: "Full hiring pipeline included",
        jobber: "Not available",
        spatchy_yes: true,
        jobber_yes: false,
        jobber_note: "No native hiring or applicant tracking",
      },
      {
        label: "Payroll management",
        spatchy: "Included on all plans",
        jobber: "Requires Gusto integration (separate subscription)",
        spatchy_yes: true,
        jobber_yes: false,
        jobber_note: "Native payroll not included — third-party required",
      },
      {
        label: "Time-off tracking",
        spatchy: "Included on all plans",
        jobber: "Not available natively",
        spatchy_yes: true,
        jobber_yes: false,
      },
    ],
  },
  {
    category: "Resources & Equipment",
    features: [
      {
        label: "Vehicle & equipment management",
        spatchy: "Included on all plans",
        jobber: "Not available",
        spatchy_yes: true,
        jobber_yes: false,
        jobber_note: "No native resource management",
      },
    ],
  },
];

const CALLOUTS = [
  {
    label: "Priced in your local currency",
    description: "Spatchy bills US customers in USD and Canadian customers in CAD. Jobber bills everyone in USD — which costs Canadian businesses an extra 35–40% on every invoice once the exchange rate hits. Whichever side of the border you're on, the number you see is the number you pay.",
    icon: "💵",
  },
  {
    label: "Route optimization on every plan",
    description: "Jobber locks route optimization behind their Grow plan at $97 USD/mo. Spatchy includes full geographic route optimization on every plan — daily and weekly, with Google Maps navigation built in. Fit more jobs into every day without upgrading.",
    icon: "🗺️",
  },
  {
    label: "Every feature. Every plan.",
    description: "Jobber locks QuickBooks sync, job costing, automation, and time tracking behind higher tiers. Spatchy includes everything on every plan. Your plan size reflects your team size — not how many features you're allowed to use.",
    icon: "🔓",
  },
  {
    label: "Email marketing included",
    description: "Jobber charges an extra $79 USD/mo for their Marketing Suite — over $110 CAD/mo just to send email campaigns. Spatchy's email marketing is built in and included on every plan at no extra charge.",
    icon: "📧",
  },
];

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export default function ComparisonPage() {
  const [openCategory, setOpenCategory] = useState(null);
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const t = setTimeout(() => setAnimated(true), 100);
    return () => clearTimeout(t);
  }, []);

  const toggleCategory = (cat) => {
    setOpenCategory(prev => prev === cat ? null : cat);
  };

  return (
    <div className="bg-slate-900 min-h-screen text-white font-sans selection:bg-blue-500 selection:text-white">
      <Helmet>
        <title>Spatchy vs Jobber | The honest comparison for Canadian businesses</title>
        <meta
          name="description"
          content="See exactly how Spatchy and Jobber compare on features and pricing. Route optimization, QuickBooks sync, and payroll included on every Spatchy plan. All data sourced from Jobber's published pricing."
        />
      </Helmet>

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <section className="pt-24 pb-20 px-6 text-center max-w-4xl mx-auto">
        <div className={`transition-all duration-700 ${animated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 text-blue-400 text-xs font-bold uppercase tracking-wide mb-6">
            Honest Comparison
          </div>
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 leading-tight">
            Spatchy vs Jobber
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-4">
            A straightforward look at what each platform offers — and what it actually costs Canadian businesses.
          </p>
          <p className="text-slate-500 text-sm max-w-xl mx-auto mb-8">
            No spin. No cherry-picking. All Jobber pricing and feature data sourced directly from getjobber.com/pricing as of March 2026.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={APP_SIGNUP_URL}
              className="px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-full transition-all shadow-lg shadow-blue-900/50 flex items-center gap-2"
            >
              Try Spatchy Free <ArrowRight size={18} />
            </a>
            <Link
              to="/pricing"
              className="px-8 py-4 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold rounded-full transition-all"
            >
              See Spatchy Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* ── KEY DIFFERENTIATORS ───────────────────────────────────────────────── */}
      <section className="px-6 mb-24">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-3">The differences that matter most</h2>
            <p className="text-slate-400"></p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {CALLOUTS.map((item, i) => (
              <div
                key={i}
                className="bg-slate-800/50 border border-white/10 rounded-2xl p-8 hover:border-blue-500/30 transition-colors"
              >
                <div className="text-3xl mb-4">{item.icon}</div>
                <h3 className="text-lg font-bold text-white mb-3">{item.label}</h3>
                <p className="text-slate-400 leading-relaxed text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING SNAPSHOT ──────────────────────────────────────────────────── */}
      <section className="px-6 mb-24">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-3">Pricing at a glance</h2>
            <p className="text-slate-400 text-sm">
              Jobber prices shown in USD as published. CAD estimates based on ~1.40 exchange rate — your actual cost will vary.
            </p>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-white/10">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-5 text-slate-400 font-medium text-sm w-1/3">Team size</th>
                  <th className="p-5 text-center">
                    <div className="text-blue-400 font-bold text-sm uppercase tracking-wide mb-1">Spatchy</div>
                    <div className="text-xs text-slate-500">Priced in CAD</div>
                  </th>
                  <th className="p-5 text-center">
                    <div className="text-slate-300 font-bold text-sm uppercase tracking-wide mb-1">Jobber</div>
                    <div className="text-xs text-slate-500">Priced in USD</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  { size: "1 user (Solo)", spatchy: "$59 CAD/mo", jobber: "$29 USD/mo (~$41 CAD)", jobberNote: "Core plan — routing, automation, and QuickBooks not included" },
                  { size: "Up to 5 users", spatchy: "$149 CAD/mo", jobber: "$65 USD/mo (~$91 CAD)", jobberNote: "Connect plan — QuickBooks & automations unlocked; routing still locked" },
                  { size: "Up to 10 users", spatchy: "$299 CAD/mo", jobber: "$97 USD/mo (~$136 CAD)", jobberNote: "Grow plan — routing, job costing & SMS unlocked at this tier" },
                  { size: "Up to 20 users", spatchy: "$499 CAD/mo", jobber: "$344 USD/mo (~$482 CAD)", jobberNote: "Plus plan" },
                ].map((row, i) => (
                  <tr key={i} className={`border-b border-white/5 ${i % 2 === 0 ? 'bg-white/[0.02]' : ''}`}>
                    <td className="p-5 text-slate-300 text-sm font-medium">{row.size}</td>
                    <td className="p-5 text-center">
                      <div className="text-white font-bold">{row.spatchy}</div>
                      <div className="text-xs text-green-400 mt-1">All features included</div>
                    </td>
                    <td className="p-5 text-center">
                      <div className="text-slate-300 font-bold">{row.jobber}</div>
                      <div className="text-xs text-slate-500 mt-1 max-w-[200px] mx-auto">{row.jobberNote}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Email marketing add-on callout */}
          <div className="mt-4 p-4 bg-amber-900/20 border border-amber-700/30 rounded-xl flex items-start gap-3">
            <AlertCircle size={18} className="text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-200/80">
              <strong className="text-amber-300">Add-ons not included above:</strong> Jobber's Marketing Suite (email campaigns, review requests, referral program) costs an additional $79 USD/mo (~$111 CAD/mo) on all plans. Spatchy includes email marketing on every plan at no extra charge.
            </p>
          </div>
        </div>
      </section>

      {/* ── FEATURE COMPARISON TABLE ──────────────────────────────────────────── */}
      <section className="px-6 mb-24">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-3">Feature by feature</h2>
            <p className="text-slate-400 text-sm">
              Jobber feature availability sourced from their published pricing page, March 2026.
            </p>
          </div>

          <div className="space-y-3">
            {COMPARISON_ROWS.map((section) => (
              <div key={section.category} className="rounded-2xl border border-white/10 overflow-hidden">
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(section.category)}
                  className="w-full flex items-center justify-between p-5 bg-slate-800/50 hover:bg-slate-800 transition-colors text-left"
                >
                  <span className="font-bold text-white">{section.category}</span>
                  <ChevronDown
                    size={18}
                    className={`text-slate-400 transition-transform duration-200 ${openCategory === section.category ? 'rotate-180' : ''}`}
                  />
                </button>

                {/* Rows */}
                <div className={openCategory === section.category ? 'hidden' : 'block'}>
                  {/* Column headers */}
                  <div className="grid grid-cols-3 border-b border-white/5 bg-slate-900/30">
                    <div className="p-3 text-xs text-slate-500 font-medium">Feature</div>
                    <div className="p-3 text-xs text-blue-400 font-bold text-center uppercase tracking-wide">Spatchy</div>
                    <div className="p-3 text-xs text-slate-400 font-bold text-center uppercase tracking-wide">Jobber</div>
                  </div>

                  {section.features.map((row, i) => (
                    <div
                      key={i}
                      className={`grid grid-cols-3 border-b border-white/5 last:border-0 items-start ${i % 2 === 0 ? 'bg-white/[0.01]' : ''}`}
                    >
                      {/* Feature label */}
                      <div className="p-4 text-sm text-slate-300 font-medium">{row.label}</div>

                      {/* Spatchy */}
                      <div className="p-4 flex flex-col items-center gap-1">
                        <CheckCircle2 size={18} className="text-green-500 flex-shrink-0" />
                        <span className="text-xs text-slate-400 text-center">{row.spatchy}</span>
                      </div>

                      {/* Jobber */}
                      <div className="p-4 flex flex-col items-center gap-1">
                        {row.jobber_yes ? (
                          <CheckCircle2 size={18} className="text-slate-400 flex-shrink-0" />
                        ) : (
                          <XCircle size={18} className="text-red-500/70 flex-shrink-0" />
                        )}
                        <span className="text-xs text-slate-500 text-center">{row.jobber}</span>
                        {row.jobber_note && (
                          <span className="text-xs text-amber-500/70 text-center italic">{row.jobber_note}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Source disclaimer */}
          <div className="mt-8 p-4 bg-slate-800/30 border border-white/5 rounded-xl">
            <p className="text-xs text-slate-500 text-center leading-relaxed">
              All Jobber feature and pricing information sourced from{' '}
              <a href="https://www.getjobber.com/pricing/" target="_blank" rel="noopener noreferrer" className="text-slate-400 underline hover:text-slate-300">
                getjobber.com/pricing
              </a>
              {' '}as of March 2026. Pricing displayed in USD as published by Jobber. CAD estimates use an approximate 1.40 exchange rate and are for illustration only. Visit getjobber.com for current and accurate pricing. Spatchy pricing in CAD.
            </p>
          </div>
        </div>
      </section>

      {/* ── WHERE JOBBER WINS ─────────────────────────────────────────────────── */}
      <section className="px-6 mb-24">
        <div className="max-w-3xl mx-auto bg-slate-800/30 border border-white/10 rounded-2xl p-8 md:p-10">
          <h2 className="text-2xl font-bold mb-4">Where Jobber has the edge</h2>
          <p className="text-slate-400 leading-relaxed mb-6">
            We believe in being honest. Jobber has been in the market since 2011 and has genuine strengths worth acknowledging.
          </p>
          <ul className="space-y-4 text-slate-300 text-sm leading-relaxed">
            <li className="flex items-start gap-3">
              <CheckCircle2 size={16} className="text-slate-400 flex-shrink-0 mt-0.5" />
              <span><strong className="text-white">Established track record.</strong> Jobber has over a decade in the market and tens of thousands of customers. Spatchy is newer. If longevity and market scale are your primary criteria, that's a fair consideration and we respect it.</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle2 size={16} className="text-slate-400 flex-shrink-0 mt-0.5" />
              <span><strong className="text-white">Larger partner ecosystem.</strong> Jobber has a broader library of third-party integrations built up over many years. Spatchy integrates with the tools most service businesses actually use — QuickBooks, Stripe, Google, and Microsoft — and we're adding more.</span>
            </li>
          </ul>
        </div>
      </section>

      {/* ── BOTTOM CTA ────────────────────────────────────────────────────────── */}
      <section className="px-6 pb-32 text-center">
        <div className="max-w-4xl mx-auto bg-gradient-to-br from-blue-900/40 to-slate-900 border border-blue-500/20 rounded-3xl p-12 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-[80px] -z-10" />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            See for yourself
          </h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">
            {/* wave1-fix 9e5d3b08: real trial is 30 days, not 14 */}
            30-day free trial. No credit card required. Every feature included from day one — including route optimization.
          </p>
          <a
            href={APP_SIGNUP_URL}
            className="inline-flex px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-full transition-all shadow-lg shadow-blue-900/50 items-center gap-2"
          >
            Start Free Trial <ArrowRight size={18} />
          </a>
          <p className="text-slate-600 text-sm mt-4">Cancel anytime. No questions asked.</p>
        </div>
      </section>
    </div>
  );
}